import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';
import {
  fetchConversations,
  fetchMessages,
  sendMessage,
  markMessagesRead,
} from '@/lib/messages';
import type { Conversation, Message } from '@/lib/messages';
import ConversationList from './components/ConversationList';
import ChatWindow from './components/ChatWindow';
import NewConversationModal from './components/NewConversationModal';

function MessagesPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const openId = (location.state as { openId?: string } | null)?.openId;

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loadingConv, setLoadingConv] = useState(true);
  const [loadingMsg, setLoadingMsg] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showNew, setShowNew] = useState(false);

  const activeIdRef = useRef<string | null>(null);
  useEffect(() => {
    activeIdRef.current = activeId;
  }, [activeId]);

  const openedFromListing = useRef(false);
  useEffect(() => {
    if (user && openId && !openedFromListing.current) {
      openedFromListing.current = true;
      setActiveId(openId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, openId]);

  const loadConversations = useCallback(async () => {
    if (!user) return;
    try {
      const data = await fetchConversations(user.id);
      setConversations(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Une erreur est survenue.');
    } finally {
      setLoadingConv(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    loadConversations();
  }, [user, loadConversations]);

  // Realtime subscription on messages
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel('messages-realtime')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages' },
        (payload) => {
          const newMsg = payload.new as Message;
          if (newMsg.conversation_id === activeIdRef.current) {
            setMessages((prev) => {
              if (prev.some((m) => m.id === newMsg.id)) return prev;
              return [...prev, newMsg];
            });
            if (newMsg.sender_id !== user.id) {
              markMessagesRead(newMsg.conversation_id, user.id).catch(() => {});
            }
          }
          loadConversations();
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, loadConversations]);

  // Load messages for the active conversation
  useEffect(() => {
    if (!activeId || !user) {
      setMessages([]);
      return;
    }
    let mounted = true;
    setLoadingMsg(true);
    fetchMessages(activeId)
      .then((data) => {
        if (!mounted) return;
        setMessages(data);
        setLoadingMsg(false);
        markMessagesRead(activeId, user.id).catch(() => {});
        setConversations((prev) =>
          prev.map((c) => (c.id === activeId ? { ...c, unread_count: 0 } : c)),
        );
      })
      .catch((e) => {
        if (!mounted) return;
        setError(e instanceof Error ? e.message : 'Une erreur est survenue.');
        setLoadingMsg(false);
      });
    return () => {
      mounted = false;
    };
  }, [activeId, user]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/10">
          <i className="ri-loader-4-line text-2xl text-primary-400 animate-spin" />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="w-full px-4 md:px-8 lg:px-12 pt-24 md:pt-32 pb-16 text-center">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
            <i className="ri-lock-line text-2xl text-foreground-400" />
          </div>
          <h1 className="font-heading font-bold text-2xl text-foreground-50 mb-2">
            Connexion requise
          </h1>
          <p className="text-sm text-foreground-400 mb-6">
            Connectez-vous pour accéder à votre messagerie.
          </p>
          <button
            onClick={() => navigate('/auth/login')}
            className="px-6 py-3 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors whitespace-nowrap text-sm"
          >
            Se connecter
          </button>
        </div>
      </div>
    );
  }

  const activeConversation = conversations.find((c) => c.id === activeId) ?? null;

  const handleSend = async (content: string) => {
    if (!activeId || !user) return;
    const msg = await sendMessage(activeId, user.id, content);
    setMessages((prev) => [...prev, msg]);
    loadConversations();
  };

  const handleStarted = (conversationId: string) => {
    loadConversations();
    setActiveId(conversationId);
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <div className="w-full px-4 md:px-8 lg:px-12 pt-20 md:pt-24 pb-4">
        <div className="h-[calc(100vh-112px)] md:h-[calc(100vh-128px)] border border-background-200/70 rounded-xl overflow-hidden flex">
          {/* Conversation list */}
          <div
            className={`${activeConversation ? 'hidden lg:flex' : 'flex'} w-full lg:w-[340px] shrink-0`}
          >
            <ConversationList
              conversations={conversations}
              activeId={activeId}
              loading={loadingConv}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              onSelect={(c) => setActiveId(c.id)}
              onNewConversation={() => setShowNew(true)}
            />
          </div>

          {/* Chat window */}
          <div className={`${activeConversation ? 'flex' : 'hidden'} lg:flex flex-1 min-w-0`}>
            {activeConversation ? (
              <ChatWindow
                conversation={activeConversation}
                messages={messages}
                currentUserId={user.id}
                loading={loadingMsg}
                onSend={handleSend}
                onBack={() => setActiveId(null)}
              />
            ) : (
              <div className="flex-1 hidden lg:flex items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                    <i className="ri-chat-3-line text-2xl text-foreground-400" />
                  </div>
                  <p className="text-foreground-400">Sélectionnez une conversation</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-red-500 text-white text-sm px-4 py-2.5 rounded-lg shadow-lg">
          {error}
        </div>
      )}

      <NewConversationModal
        open={showNew}
        currentUserId={user.id}
        onClose={() => setShowNew(false)}
        onStarted={handleStarted}
      />
    </div>
  );
}

export default MessagesPage;